import React, { useCallback, useEffect, useState } from 'react';
import { useHOA } from '../../../contexts/HOAContext';
import { EmptyState, StatusPill } from '../../ui';
import { associationService } from '../../../services/associationService';
import { LeadCard } from './LeadCard';

const inputClass = 'w-full rounded-2xl border border-amber-100/60 bg-white px-4 py-2.5 text-sm font-medium text-primary-dark focus:outline-none focus:ring-2 focus:ring-secondary/40';

// Atrionos staff only (AdminPortal shows the tab to platform owners): every
// association on the platform, and the onboarding pipeline that opens new ones.
export function AssociationsPanel() {
  const { hoa } = useHOA();
  const [associations, setAssociations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newLead, setNewLead] = useState({ organization: '', communityAddress: '', contactEmail: '' });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, l] = await Promise.all([associationService.listAssociations(), associationService.listLeads()]);
      setAssociations(a);
      setLeads(l);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addLead = async () => {
    setAdding(true);
    try {
      const { id } = await associationService.createLead(newLead);
      setNewLead({ organization: '', communityAddress: '', contactEmail: '' });
      if (newLead.communityAddress.trim()) await associationService.runResearch(id).then(null, () => null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-12" data-testid="associations-panel">
      <section className="bg-section-bg p-8 rounded-[2.5rem] space-y-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Atrionos · Super Admin</p>
          <h2 className="text-3xl font-bold tracking-tight text-primary-dark">Associations</h2>
        </div>
        {error && <p className="text-sm font-medium text-red-600">{error}</p>}
        {loading ? (
          <p className="text-sm font-medium text-slate-500">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {associations.map((a) => (
              <article key={a.id} className="bg-white rounded-2xl p-6 warm-tactile-shadow border border-amber-100/30 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-bold text-primary-dark">{a.name}</h3>
                    <p className="text-[10px] font-bold uppercase tracking-tighter text-slate-500">
                      {a.slug}.atrionos.com · {[a.city, a.state].filter(Boolean).join(', ')}
                    </p>
                  </div>
                  <StatusPill
                    label={a.subscription_status === 'trialing'
                      ? `Trial${a.trial_ends_at ? ` to ${new Date(a.trial_ends_at).toLocaleDateString()}` : ''}`
                      : (a.subscription_status || 'active')}
                    variant={a.subscription_status === 'trialing' ? 'pending' : 'active'}
                  />
                </div>
                <div className="flex gap-6">
                  <div><div className="text-2xl font-extrabold text-secondary">{a.property_count}</div><div className="text-[10px] font-bold uppercase tracking-tighter text-slate-500">Properties</div></div>
                  <div><div className="text-2xl font-extrabold text-secondary">{a.member_count}</div><div className="text-[10px] font-bold uppercase tracking-tighter text-slate-500">Portal users</div></div>
                  <div><div className="text-2xl font-extrabold text-secondary">{a.subscription_tier || '—'}</div><div className="text-[10px] font-bold uppercase tracking-tighter text-slate-500">Plan</div></div>
                </div>
                <button
                  type="button"
                  disabled={a.slug === hoa?.slug}
                  onClick={() => associationService.switchTo(a.slug).then(null, (e) => setError(e.message))}
                  className="border border-secondary text-secondary px-6 py-2.5 rounded-full text-sm font-bold hover:bg-secondary hover:text-white transition-all disabled:opacity-50"
                >
                  {a.slug === hoa?.slug ? 'You are here' : 'Open portal'}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="bg-section-bg p-8 rounded-[2.5rem] space-y-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Onboarding</p>
          <h2 className="text-3xl font-bold tracking-tight text-primary-dark">Leads</h2>
          <p className="text-sm font-medium text-slate-500 max-w-2xl mt-1">
            Website requests that include a home address are researched automatically. Add a lead by hand
            when you already know an address in the community.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 warm-tactile-shadow border border-amber-100/30 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <label className="text-sm font-medium text-slate-600">Association
            <input className={inputClass} value={newLead.organization} onChange={(e) => setNewLead({ ...newLead, organization: e.target.value })} />
          </label>
          <label className="text-sm font-medium text-slate-600 md:col-span-2">A home address in the community
            <input className={inputClass} placeholder="Street, city, MT ZIP" value={newLead.communityAddress}
              onChange={(e) => setNewLead({ ...newLead, communityAddress: e.target.value })} />
          </label>
          <button
            type="button"
            disabled={adding || !newLead.organization.trim()}
            onClick={addLead}
            className="bg-secondary text-white px-6 py-2.5 rounded-full text-sm font-bold hover:brightness-110 active:scale-95 transition-all disabled:opacity-50"
          >
            {adding ? 'Adding…' : 'Add lead'}
          </button>
        </div>

        {!loading && leads.length === 0 ? (
          <EmptyState icon="person_add" title="No leads yet" body="Requests from the atrionos.com form appear here, already researched when they include an address." />
        ) : (
          <div className="space-y-6">
            {leads.map((lead) => <LeadCard key={lead.id} lead={lead} associations={associations} onChanged={load} />)}
          </div>
        )}
      </section>
    </div>
  );
}

export default AssociationsPanel;
